
import * as React from 'react';

import { Alert, Background, Container, Icon, Text } from '@/components/ui';
import SignupPlans from './Content/SignupPlans';
import Plans from './Content/Plans';

export function FeatureList ({ features, iconTone = 'primary' }) {
    return (
        <ul className="tw-space-y-0.5 tw-clear-float-list">
            {features.map((feature, i) => {
                if (feature.type === 'spacer') {
                    return <li key={`spacer-${i}`} className="tw-relative tw-pl-6 tw-inline-block tw-opacity-0">NA</li>
                }
                return (
                    <li key={i} className="tw-relative tw-pl-6 tw-inline-block tw-m-0">
                        <span className={`tw-relative tw-z-lift`}>{feature}</span>
                        <span className="tw-absolute tw-left-0 tw-top-1/2 tw-z-0 tw--translate-y-1/2">
                            <Icon icon="check" size="small" tone={iconTone} />
                        </span>
                    </li>
                )
            })}
        </ul>
    )
}

export function ConditionsList ({ conditions, iconTone = 'primary' }) {
    return (
        <ul className="tw-space-y-0.5 tw-clear-float-list">
            {conditions.map((condition, i) => (
                <li key={i} className="tw-relative tw-pl-6 tw-inline-block tw-m-0">
                    <span className={`tw-relative tw-z-lift`}>{condition}</span>
                    <span className="tw-absolute tw-left-0 tw-top-1/2 tw-z-0 tw--translate-y-1/2">
                        <Icon icon="circleChevronRight" size="small" tone={iconTone} />
                    </span>
                </li>
            ))}
        </ul>
    )
}

export function ExtraSection ({ extra }) {
    if (!extra) {
        return null
    }
    return (
        <div className="tw-mt-4">
            <Text as="p" tone="inherit" variant="body-sm"><strong>{extra.title}</strong></Text>
            {extra.text.map((line, i) => (
                <Text as="p" key={i} tone="inherit" variant="body-sm">{line}</Text>
            ))}
        </div>
    )
}

export function getAllowedTransitions (planSlug) {
    return {
        free: true,
        essentials: ['free'].includes(planSlug),
        standard: ['free', 'essentials'].includes(planSlug),
        advanced: ['free', 'essentials', 'standard'].includes(planSlug),
        premium: ['free', 'essentials', 'standard', 'advanced'].includes(planSlug)
    }
}

export function addSpacersToFeatures (features, plans) {
    const premiumPlan = plans.find(plan => plan.slug === 'premium')
    const maxCount = premiumPlan ? premiumPlan.features.length : features.length
    const spacersNeeded = maxCount - features.length
    if (spacersNeeded <= 0) {
        return features
    }

    return [
        ...features,
        ...Array(spacersNeeded).fill({ type: 'spacer', content: '' })
    ]
}

export default function PlansIndex ({ organization, currentPlan, noAccess }) {
    const plans = React.useMemo(() => [
        {
            key: i18next.t('plans.free'),
            slug: 'free',
            price: '0',
            features: [
                i18next.t('plans.features.data_analysis'),
                i18next.t('plans.features.alarms'),
                i18next.t('plans.features.utilities_costs_invoices'),
                i18next.t('plans.features.free_reports')
            ],
            conditions: [
                i18next.t('plans.conditions.single_user'),
                i18next.t('plans.conditions.data_retention', { years: 3 }),
                i18next.t('plans.conditions.max_1_wise_integrations'),
                i18next.t('plans.conditions.max_sites', { sites: 5 }),
                i18next.t('plans.conditions.max_indicators', { indicators: 1 })
            ],
            extra: {
                title: i18next.t('plans.allows_up_to'),
                text: [i18next.t('plans.features.5_sites'), i18next.t('plans.features.5_indicators')]
            }
        },
        {
            key: i18next.t('plans.essentials'),
            slug: 'essentials',
            price: '5',
            features: [
                i18next.t('plans.features.data_analysis'),
                i18next.t('plans.features.alarms'),
                i18next.t('plans.features.utilities_costs_invoices'),
                i18next.t('plans.features.free_reports')
            ],
            conditions: [
                i18next.t('plans.conditions.unlimited_users'),
                i18next.t('plans.conditions.data_retention', { years: 3 }),
                i18next.t('plans.conditions.max_1_wise_integrations'),
                i18next.t('plans.conditions.contract', { years: 1 }),
                i18next.t('plans.conditions.no_wisebox'),
                i18next.t('plans.conditions.min_sites', { sites: 50 })
            ],
            extra: {
                title: i18next.t('plans.allows_up_to'),
                text: [i18next.t('plans.features.5_sites'), i18next.t('plans.features.5_indicators')]
            }
        },
        {
            key: i18next.t('plans.standard'),
            slug: 'standard',
            price: '45',
            features: [
                i18next.t('plans.features.data_analysis'),
                i18next.t('plans.features.alarms'),
                i18next.t('plans.features.opportunities'),
                i18next.t('plans.features.utilities_costs_invoices'),
                i18next.t('plans.features.objectives'),
                i18next.t('plans.features.utilities_management'),
                i18next.t('plans.features.standard_reports')
            ],
            conditions: [
                i18next.t('plans.conditions.unlimited_users'),
                i18next.t('plans.conditions.data_retention', { years: 3 }),
                i18next.t('plans.conditions.wisebox_4g'),
                i18next.t('plans.conditions.max_wise_integrations', { integrations: 2 }),
                i18next.t('plans.conditions.contract', { years: 2 })
            ]
        },
        {
            key: i18next.t('plans.advanced'),
            slug: 'advanced',
            price: '65',
            features: [
                i18next.t('plans.features.data_analysis'),
                i18next.t('plans.features.alarms'),
                i18next.t('plans.features.opportunities'),
                i18next.t('plans.features.utilities_costs_invoices'),
                i18next.t('plans.features.objectives'),
                i18next.t('plans.features.utilities_management'),
                i18next.t('plans.features.advanced_reports'),
                i18next.t('plans.features.tenants'),
                i18next.t('plans.features.iso_50001_processes')
            ],
            conditions: [
                i18next.t('plans.conditions.unlimited_users'),
                i18next.t('plans.conditions.data_retention', { years: 6 }),
                i18next.t('plans.conditions.wisebox_4g'),
                i18next.t('plans.conditions.advanced_api'),
                i18next.t('plans.conditions.max_wise_integrations', { integrations: 5 }),
                i18next.t('plans.conditions.contract', { years: 2 })
            ]
        },
        {
            key: i18next.t('plans.premium'),
            slug: 'premium',
            price: '85',
            features: [
                i18next.t('plans.features.data_analysis'),
                i18next.t('plans.features.alarms'),
                i18next.t('plans.features.opportunities'),
                i18next.t('plans.features.utilities_costs_invoices'),
                i18next.t('plans.features.objectives'),
                i18next.t('plans.features.utilities_management'),
                i18next.t('plans.features.premium_reports'),
                i18next.t('plans.features.tenants'),
                i18next.t('plans.features.iso_50001_processes'),
                i18next.t('plans.features.technical_support')
            ],
            conditions: [
                i18next.t('plans.conditions.unlimited_users'),
                i18next.t('plans.conditions.perpetual_retention'),
                i18next.t('plans.conditions.wisebox_4g'),
                i18next.t('plans.conditions.premium_api'),
                i18next.t('plans.conditions.max_wise_integrations_unlimited'),
                i18next.t('plans.conditions.contract', { years: 2 })
            ],
            extra: {
                title: i18next.t('plans.special_offer'),
                text: [i18next.t('plans.features.wisestore_integrations'), i18next.t('plans.features.discount_10')]
            }
        }
    ], [])

    if (!organization) {
        return <SignupPlans plans={plans}/>
    }

    if (noAccess) {
        return (
            <Background heightOffset={90}>
                <Container variant="row">
                    <div className="tw-flex-1 tw-min-w-0">
                        <Alert
                            tone="warning"
                            title={i18next.t('plans.no_access_title')}
                            description={WiseMetering.Permissions.hasRolePermissions('plans', 'update') ? i18next.t('plans.no_access') : i18next.t('plans.no_access_extra')}
                        />
                        <Plans organization={organization} currentPlan={currentPlan} plans={plans}/>
                    </div>
                </Container>
            </Background>
        )
    }

    return (
        <Background heightOffset={90}>
            <Container variant="row">
                <div className="tw-flex-1 tw-min-w-0">
                    <Plans organization={organization} currentPlan={currentPlan} plans={plans}/>
                </div>
            </Container>
        </Background>
    )
}
